<script>
import RexTableCompV2 from '@/rex-components/rex-TableCompV2.vue';
import { LOGService } from '@/services/api/log.service';
import { mapGetters, mapMutations, mapState } from 'vuex';
import { getTimeZone } from '@/utils/time/timezone';
import { TABLE_PAGE_SIZE } from '@/components/Home/utils/custom.home.types';
import { getRandomUniqueId } from '@/utils/helper-function';
import { formatDate } from '@/utils/time/formatDate';
import { getTimeZoneOffset } from '@/utils/time/timeZoneOffset';
import {
  timeStamp,
  timeStampEpoch,
  timeStampFormat,
} from '@/components/Log/log.constants';
import copy from '@/utils/copy';
import { notificationSucess } from '@/utils/notification';
import { CAPABILITY_LOG, CAPABILITY_ANALYTICS } from '@/utils/constants';
import {
  VIEW_SAVED_SEARCHES_TAB_KEY,
  LM_CAPABILITY,
  ANALYTICS_CAPABILITY,
} from '@/components/Log/log.constants';
import { cusGraphCapabilityLabelMapper } from '@/components/charts/util';
import { usePermissions } from '@/hooks/usePermissions';
import { HOME_RESOURCE_GROUP } from '@/router/constant';

export default {
  components: { RexTableCompV2 },
  props: {
    table: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapState('globalState/user', ['teamID']),
    ...mapGetters('globalState/settings', ['getDurationTime']),
    ...mapState('globalState/settings', ['timezoneoffset']),
    dispInfiniteFooter() {
      return this.stopInfiniteScroll && this.items?.length > 18;
    },
    dispScrollFooter() {
      return this.items?.length > 18;
    },
    metricInfoLabel() {
      return this.table?.header?.metricInfoLabel;
    },
    getCapabilityType() {
      return this.table.capability === CAPABILITY_ANALYTICS
        ? ANALYTICS_CAPABILITY
        : LM_CAPABILITY;
    },
    hasWritePermission() {
      const { hasCapabilityWritePermission } = usePermissions();

      return hasCapabilityWritePermission(HOME_RESOURCE_GROUP);
    },
    getCapabilityLabel() {
      return (
        cusGraphCapabilityLabelMapper[this.table.capability] ||
        this.table.capability
      );
    },
  },
  data() {
    return {
      headers: [],
      items: [],
      loading: false,
      stopInfiniteScroll: false,
      currentPage: 1,
      offset: 0,
      isError: false,
    };
  },
  methods: {
    ...mapMutations('homeState', ['setdeleteWidgetId']),
    getRandomUniqueId,
    formatTableResp(list) {
      return list.map((item) => {
        if (timeStamp in item) {
          item[timeStamp] =
            formatDate(item[timeStamp], this.timezoneoffset, timeStampFormat) +
            getTimeZoneOffset(this.timezoneoffset);
        }
        if (timeStampEpoch in item) {
          item[timeStampEpoch] =
            formatDate(
              item[timeStampEpoch],
              this.timezoneoffset,
              timeStampFormat,
            ) + getTimeZoneOffset(this.timezoneoffset);
        }
        return { _id: `${getRandomUniqueId()}`, ...item };
      });
    },
    async getTableInfo() {
      try {
        this.loading = true;
        const { data } = await LOGService.applySqlQuery({
          service_id: this.table.serviceKey,
          limit: this.pageSize,
          timeZone: getTimeZone(this.timezoneoffset),
          team_id: this.teamID,
          duration: this.getDurationTime,
          offset: (this.currentPage - 1) * this.pageSize,
          capability: this.getCapabilityType,
        });

        if (data.success) {
          this.items = [...this.items, ...this.formatTableResp(data.data.rows)];
          this.offset = this.offset + this.pageSize;
          if (data.data.rows?.length < this.pageSize) {
            this.stopInfiniteScroll = true;
          }
          if (!this.headers || this.headers.length === 0) {
            this.headers = data.data.columns
              .slice(0, 8)
              .map((item) => ({ key: item, name: item, width: '12.5' }));
          }
        }
        this.loading = false;
      } catch (error) {
        this.isError = true;
        this.stopInfiniteScroll = true;
        this.loading = false;
        console.log('error', error);
      }
    },
    handleExpandRow(index) {
      this.items.map((obj, i) => {
        obj.showDetails = i === index ? !obj.showDetails : false;
        return obj;
      });
    },
    handleCopy(content) {
      delete content.showDetails;
      delete content._id;
      copy(JSON.stringify(content));
      notificationSucess(this.$t('copied'));
    },
    getDetailedLog(item) {
      const newItem = { ...item };
      delete newItem.showDetails;
      delete newItem._id;
      return newItem;
    },
    resetTableData() {
      this.currentPage = 1;
      this.items = [];
      this.stopInfiniteScroll = false;
      this.loading = false;
      this.offset = 0;
    },
    deleteWidget() {
      this.setdeleteWidgetId(this.table.id);
    },
    handleRedirect(capability) {
      switch (capability) {
        case CAPABILITY_LOG:
          this.$router.push({
            name: 'log',
            query: { currView: VIEW_SAVED_SEARCHES_TAB_KEY },
          });
          break;
        case CAPABILITY_ANALYTICS:
          this.$router.push({
            name: 'analytics',
            query: { currView: VIEW_SAVED_SEARCHES_TAB_KEY },
          });
          break;
      }
    },
  },
  watch: {
    getDurationTime() {
      this.resetTableData();
      this.getTableInfo();
    },
  },
  mounted() {
    this.pageSize = TABLE_PAGE_SIZE;
    this.getTableInfo();
    this.currentPage++;
  },
};
</script>

<template>
  <div class="table-widget-box">
    <RexTableCompV2
      :headers="headers"
      :items="items"
      :enableDetails="true"
      :loading="loading"
      @fetchNextPageData="
        () => {
          if (items.length > 0 && headers.length > 0 && !stopInfiniteScroll) {
            getTableInfo();
            currentPage++;
          }
        }
      "
      :hideRows="headers && headers.length === 0"
      :enableScrollFooter="dispScrollFooter"
      :striped="true"
      :enableInfiniteScroll="true"
      :showTitle="true"
      :title="table.widgetName"
    >
      <template #table-title>
        <div class="table-widget-title-box">
          <div class="table-widget-title-delete">
            <span>{{ table.widgetName }}</span>
            <span
              v-if="hasWritePermission"
              @click="deleteWidget"
              class="delete-table-icon"
            >
              <BaseIcon
                name="deleteFilled"
                width=".9125rem"
                height=".9125rem"
              />
            </span>
          </div>
          <div>
            <BaseIcon
              class="cus-table-metric-cap-icon"
              :name="table.capability.toLowerCase()"
              width=".9125rem"
              height=".9125rem"
            />
            <span class="cus-table-metric-cap-text">
              {{ getCapabilityLabel }}
            </span>
            <span
              class="table-metric-info-label"
              @click="() => handleRedirect(table.capability)"
            >
              {{ metricInfoLabel }}
            </span>
          </div>
        </div>
      </template>

      <template
        v-for="(header, i) in headers"
        #[`body-${header.key}`]="{ item, index }"
        :key="`${header.key}-${getRandomUniqueId()}`"
      >
        <div v-if="i === 0">
          <BaseIcon
            name="chevronRightIcon"
            @click="() => handleExpandRow(index)"
            height="0.8em"
            width="0.8em"
            class="base-icon"
            :style="item.showDetails ? 'transform: rotate(90deg);' : ''"
          />
        </div>
        <div class="ellipsis-content">
          {{ item[header.key] }}
        </div>
      </template>

      <template v-if="headers.length > 0" #row-details="{ item }">
        <div class="row-details">
          <div class="details-body">
            <pre class="details-body-text">{{ getDetailedLog(item) }}</pre>
          </div>
          <div
            class="copy-icon"
            v-tooltip:right="$t('copy')"
            @click="handleCopy(item)"
          >
            <BaseIcon name="copyNew" iconColor="#f59600" />
          </div>
        </div>
      </template>

      <template v-if="dispInfiniteFooter" #infinite-footer>
        <div class="log-infi-footer">
          <div v-if="isError">
            {{ $t('log.errorWhileFetchingData') }}
            <button
              class="refresh-btn"
              @click="
                () => {
                  resetTableData();
                  getTableInfo();
                }
              "
            >
              <BaseIcon
                name="refresh"
                height="0.8em"
                width="0.8em"
                class="log-refresh-icon"
              />
            </button>
          </div>
          <div v-else>
            {{ $t('log.availableDataLoadedCompletely') }}
          </div>
        </div>
      </template>
    </RexTableCompV2>
  </div>
</template>

<style lang="scss" scoped>
.table-widget-title-box {
  @include flex-center-justify(space-between);
}
.table-widget-title-delete {
  @include flex-center-vert;
}
.table-metric-info-label {
  height: 1.875em;
  color: $color-brand-accent;
  cursor: pointer;
  font-family: $semi-bold;
  font-size: 0.75em;
  &:hover {
    text-decoration: underline;
  }
  margin-right: 0.8125em;
}
.table-widget-box {
  margin-bottom: 1.5rem;
}
.widget-table-header {
  color: $color-brand-accent;
  font-size: 1rem;
}
.ellipsis-content {
  @include truncate(95%);
}
.base-icon {
  margin-left: 0.75rem;
  display: inline-block;
  @include svg-fill-pointer($color-brand-accent);
  margin-right: 3.4rem;
}
.row-details {
  background-color: $color-black;
  padding: 1.12rem 0rem 0rem 4.35rem;
  margin-top: -0.1rem;
  display: flex;
  flex-direction: column-reverse;
}

.details-body-text {
  color: $color-white;
  padding-bottom: 1.53rem;
  margin-bottom: 0rem;
  margin-right: 1.7rem;
}
.details-body {
  max-height: 22.65rem;
  overflow: auto;
}
.details-body-text::-webkit-scrollbar {
  height: 0.25rem !important;
}
.loaderBox {
  width: 100%;
  height: 40rem;
  @include flex-center;
}
.copy-icon {
  margin-left: auto;
  cursor: pointer;
  margin-right: 1.7rem;
  @include svg-fill-pointer($color-brand-accent);
}
.icon-column-header {
  margin-left: 5rem;
}
.log-infi-footer {
  @include flex-center;
  color: $font-tertiary;
  padding: 1.25rem 0rem;
}
.refresh-btn {
  border: none;
  background-color: inherit;
}
.log-refresh-icon {
  @include svg-fill-pointer($color-brand-accent);
}
.delete-table-icon {
  fill: $color-brand-accent;
  cursor: pointer;
  margin-left: 0.4em;
  margin-top: -0.1em;
}
.cus-table-metric-cap-icon {
  fill: $font-primary;
  margin-right: 0.5rem;
}
.cus-table-metric-cap-text {
  color: $font-primary;
  font-family: $semi-bold;
  font-size: 0.75rem;
  margin-right: 1rem;
}
:deep(.header-container) {
  border-top: 0.0125rem solid $border-color-primary;
  background-color: $bg-secondary;
  border-radius: 0;
}
:deep(.header-td) {
  background-color: $bg-secondary;
}
:deep(.header-td:first-child > div) {
  margin-left: 5rem;
}
</style>
